/**
 * SAMPLE DATA ONLY — reports and the KPI panel of the demo mode (ADR 0018). Everything is computed from
 * the fictional demo fleet: usage, routes and checklist compliance are generated deterministically per
 * vehicle and day (relative to "now"), events are the demo alarms. Same shapes and the same visibility
 * rules as the database functions (site scope, operator names only for visible operators).
 */
import {
  EVENT_GROUP_TYPES,
  HEATMAP_LIMITS,
  heatmapAggregate,
  heatmapEventWeight,
  REPORT_LIMITS,
  checkReportRange,
  checkRouteWindow,
  localParts,
  shiftInstances,
  zonedTimeToUtc,
  type Alarm,
  type AlertHours,
  type ChecklistComplianceRow,
  type EffectiveBasis,
  type ExportFormat,
  type HeatmapResult,
  type UsageBasis,
  type Device,
  type EventRow,
  type Geofence,
  type KpiPanel,
  type KpiPeriod,
  type KpiSeriesPoint,
  type LiveSnapshot,
  type ReportResult,
  type RoutePoint,
  type Site,
  type SiteShift,
  type TenantRole,
  type UsageRow,
  type Vehicle,
} from "@fts/core";
import { DEMO_MODULE_DEVICES } from "./demo-modules";
import { DataError, type ChecklistFault, type OperatorData } from "./provider";
import type { EventReportQuery, HeatmapQuery, ReportExportEntry, ReportQuery, RouteQuery } from "./reports";

export type DemoReportsAccess = {
  now(): number;
  userId(): string | null;
  hasTenantRole(tenantId: string, min: TenantRole): boolean;
  canAccessSite(siteId: string): boolean;
  sites(): Site[];
  vehicles(): Vehicle[];
  devices(): Device[];
  live(): LiveSnapshot[];
  alarms(): Alarm[];
  alarmVisible(a: Alarm): boolean;
  operators(): OperatorData;
  geofences(): Geofence[];
  shiftsOf(siteId: string): SiteShift[];
  /** The site's working hours (own row, else inherited, else Mon–Fri 08:00–18:00). */
  workHoursOf(siteId: string): AlertHours;
  faults(tenantId: string): Promise<ChecklistFault[]>;
};

const TZ = "Europe/Istanbul";
/** Perimeter of a sample vehicle's yard loop (140 m × 70 m). */
const YARD_LOOP_M = 2 * (140 + 70);
const DAY = 86_400_000;

/** Deterministic noise in [0, 1) from a string. */
function noise(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  const x = Math.sin(h) * 43758.5453;
  return x - Math.floor(x);
}
const addDays = (day: string, n: number) => new Date(Date.parse(`${day}T12:00:00Z`) + n * DAY).toISOString().slice(0, 10);
const isoDay = (day: string) => {
  const d = new Date(`${day}T12:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
};
const dayRange = (from: string, to: string) => {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
};
const r1 = (x: number) => Math.round(x * 10) / 10;

type DayUsage = { ign: number; mov: number; basis: number; basisIgn: number; kind: EffectiveBasis; operators: string[]; lifts: number | null; kg: number | null; distM: number };

export class DemoReports {
  constructor(private readonly a: DemoReportsAccess) {}

  // Per-call caches (Intl time-zone math is slow; a KPI call evaluates thousands of vehicle-days).
  private cache = {
    windows: new Map<string, { ds: number; de: number }>(),
    usage: new Map<string, DayUsage>(),
    drivers: new Map<string, string[]>(),
    shifts: new Map<string, SiteShift[]>(),
    inst: new Map<string, { s: number; e: number }[]>(),
  };
  private fresh(): void {
    this.cache = { windows: new Map(), usage: new Map(), drivers: new Map(), shifts: new Map(), inst: new Map() };
  }
  private shiftsOf(siteId: string): SiteShift[] {
    let s = this.cache.shifts.get(siteId);
    if (!s) this.cache.shifts.set(siteId, (s = this.a.shiftsOf(siteId)));
    return s;
  }

  /* ---------- scope ---------- */

  private requireViewer(tenantId: string): void {
    if (!this.a.userId() || !this.a.hasTenantRole(tenantId, "viewer")) throw new DataError("forbidden");
  }

  private siteTree(tenantId: string, siteId: string | null): Set<string> {
    const sites = this.a.sites().filter((s) => s.tenantId === tenantId && this.a.canAccessSite(s.id));
    if (!siteId) return new Set(sites.map((s) => s.id));
    if (!sites.some((s) => s.id === siteId)) return new Set();
    const out = new Set([siteId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of sites) if (s.parentId && out.has(s.parentId) && !out.has(s.id)) (out.add(s.id), (grew = true));
    }
    return out;
  }

  private scopeVehicles(q: Pick<ReportQuery, "tenantId" | "siteId" | "vehicleIds">): Vehicle[] {
    const sites = this.siteTree(q.tenantId, q.siteId);
    return this.a.vehicles().filter((v) => v.tenantId === q.tenantId && sites.has(v.siteId) && (q.vehicleIds.length === 0 || q.vehicleIds.includes(v.id)));
  }

  private hasDevice(v: Vehicle): Device | undefined {
    return this.a.devices().find((d) => d.vehicleId === v.id);
  }

  private operatorVisible(id: string): boolean {
    const o = this.a.operators().operators.find((x) => x.id === id);
    return !!o && !o.anonymized && this.a.hasTenantRole(o.tenantId, "viewer") && (o.siteId === null || this.a.canAccessSite(o.siteId));
  }

  private ancestors(siteId: string): string[] {
    const out: string[] = [];
    let cur = this.a.sites().find((s) => s.id === siteId);
    while (cur) {
      out.push(cur.id);
      const p: string | null = cur.parentId;
      cur = p ? this.a.sites().find((s) => s.id === p) : undefined;
    }
    return out;
  }

  /** Sample operators who drive a vehicle: operators with a permission on its site (or an ancestor) or on it. */
  private driversOf(v: Vehicle): string[] {
    const hit = this.cache.drivers.get(v.id);
    if (hit) return hit;
    const anc = new Set(this.ancestors(v.siteId));
    const ops = this.a.operators();
    const out = ops.operators
      .filter((o) => o.active && !o.anonymized && o.leftOn === null)
      .filter((o) => ops.permissions.some((p) => p.operatorId === o.id && (p.vehicleId === v.id || (p.siteId !== null && anc.has(p.siteId) && (p.vehicleType === null || p.vehicleType === v.type)))))
      .map((o) => o.id);
    this.cache.drivers.set(v.id, out);
    return out;
  }

  /* ---------- sample usage ---------- */

  private dayWindow(day: string, tz = TZ): { ds: number; de: number } {
    const key = `${tz}|${day}`;
    let w = this.cache.windows.get(key);
    if (!w) this.cache.windows.set(key, (w = { ds: zonedTimeToUtc(day, "00:00", tz).getTime(), de: zonedTimeToUtc(addDays(day, 1), "00:00", tz).getTime() }));
    return w;
  }

  /** One vehicle on one local day, clipped to [from, until). Deterministic; nothing after now. */
  private usage(v: Vehicle, day: string, from = 0, until = Infinity, basis: UsageBasis = "auto"): DayUsage {
    const key = `${v.id}|${day}|${from}|${until}|${basis}`;
    const hit = this.cache.usage.get(key);
    if (hit) return hit;
    const u = this.usageRaw(v, day, from, until, basis);
    this.cache.usage.set(key, u);
    return u;
  }

  /** Basis windows of a site on one local day (shifts, working hours or the whole day), like fts_private.basis_windows. */
  private basisWindows(siteId: string, day: string, kind: EffectiveBasis): { s: number; e: number }[] {
    const { ds, de } = this.dayWindow(day);
    if (kind === "24h") return [{ s: ds, e: de }];
    if (kind === "shifts") {
      const shifts = this.shiftsOf(siteId);
      return shifts.length ? this.dayShifts(siteId, day, shifts) : [];
    }
    const h = this.a.workHoursOf(siteId);
    if (!h.workDays.includes(isoDay(day))) return [];
    const s = zonedTimeToUtc(day, h.workStart, h.tz).getTime();
    const e = h.workEnd === "24:00" ? zonedTimeToUtc(addDays(day, 1), "00:00", h.tz).getTime() : zonedTimeToUtc(day, h.workEnd, h.tz).getTime();
    return [{ s, e }];
  }

  private kindOf(siteId: string, basis: UsageBasis): EffectiveBasis {
    return basis === "auto" ? (this.shiftsOf(siteId).length ? "shifts" : "work") : basis;
  }

  private usageRaw(v: Vehicle, day: string, from: number, until: number, requested: UsageBasis): DayUsage {
    const now = this.a.now();
    const { ds, de } = this.dayWindow(day);
    const a = Math.max(ds, from);
    const b = Math.min(de, until, now);
    const kind = this.kindOf(v.siteId, requested);
    const empty: DayUsage = { ign: 0, mov: 0, basis: 0, basisIgn: 0, kind, operators: [], lifts: null, kg: null, distM: 0 };
    if (b <= a) return empty;
    // Work happens 06:00–22:00 local; the share of that window inside [a, b) is credited.
    const workA = ds + 6 * 3600_000;
    const workB = ds + 22 * 3600_000;
    const share = Math.max(0, Math.min(b, workB) - Math.max(a, workA)) / (workB - workA);
    const wd = isoDay(day);
    const n = noise(`${v.id}|${day}`);
    const hours = wd === 7 ? (n < 0.3 ? 0.6 : 0) : !v.active ? 0 : 3.2 + 4.3 * n;
    const ign = hours * 3600 * share;
    const mov = ign * (0.42 + 0.2 * noise(`${v.id}|${day}|m`));
    const basis = this.basisWindows(v.siteId, day, kind).reduce((s, i) => s + Math.max(0, Math.min(b, i.e) - Math.max(a, i.s)) / 1000, 0);
    // Sample: most of the use falls inside shifts / working hours (all of it inside a whole day).
    const basisIgn = kind === "24h" ? ign : Math.min(basis, ign * 0.93);
    const drivers = this.driversOf(v);
    const operators = ign > 0 && drivers.length ? [drivers[Math.floor(n * drivers.length) % drivers.length]!, ...(n > 0.6 && drivers.length > 1 ? [drivers[(Math.floor(n * drivers.length) + 1) % drivers.length]!] : [])] : [];
    const device = this.hasDevice(v);
    const lift = !!device && !!DEMO_MODULE_DEVICES[device.deviceId] && device.deviceId === "DEV-SMP-1001";
    const lifts = lift ? Math.round((ign / 3600) * 9) : null;
    return { ign, mov, basis, basisIgn, kind, operators, lifts, kg: lifts === null ? null : lifts * 850, distM: (mov / 3600) * (4 + 2 * noise(`${v.id}|${day}|d`)) * 1000 };
  }

  /** Shift occurrences overlapping one local day of a site, cached per call. */
  private dayShifts(siteId: string, day: string, shifts: SiteShift[]): { s: number; e: number }[] {
    const key = `${siteId}|${day}`;
    let hit = this.cache.inst.get(key);
    if (!hit) {
      const { ds, de } = this.dayWindow(day);
      hit = shiftInstances(shifts, new Date(ds), new Date(de)).map((i) => ({ s: i.startsAt.getTime(), e: i.endsAt.getTime() }));
      this.cache.inst.set(key, hit);
    }
    return hit;
  }

  private today(): string {
    return localParts(new Date(this.a.now()), TZ).date;
  }

  /* ---------- reports ---------- */

  async reportUsage(q: ReportQuery): Promise<ReportResult<UsageRow>> {
    this.fresh();
    this.requireViewer(q.tenantId);
    if (checkReportRange(q.from, q.to)) throw new DataError("invalid_report_range");
    const rows: UsageRow[] = [];
    for (const v of this.scopeVehicles(q).filter((x) => this.hasDevice(x)).sort((x, y) => x.fleetNo.localeCompare(y.fleetNo))) {
      for (const day of dayRange(q.from, q.to)) {
        const u = this.usage(v, day, 0, Infinity, q.basis ?? "auto");
        if (u.ign <= 0) continue;
        if (q.operatorId && !u.operators.includes(q.operatorId)) continue;
        const k = q.operatorId ? 1 / u.operators.length : 1;
        const visible = u.operators.filter((o) => this.operatorVisible(o));
        const ops = q.operatorId ? [q.operatorId].filter((o) => this.operatorVisible(o)) : visible;
        rows.push({
          vehicleId: v.id, fleetNo: v.fleetNo, siteId: v.siteId, day,
          ignitionS: Math.round(u.ign * k), movingS: Math.round(u.mov * k), idleS: Math.round(u.ign * k) - Math.round(u.mov * k), distanceM: Math.round(u.distM * k),
          liftCount: u.lifts === null ? null : Math.round(u.lifts * k), liftedKg: u.kg === null ? null : Math.round(u.kg * k), liftEstimated: u.lifts === null ? null : false,
          operatorIds: ops,
          operatorNames: ops.map((o) => this.a.operators().operators.find((x) => x.id === o)!.fullName).sort(),
          operatorsHidden: (q.operatorId ? 1 : u.operators.length) - ops.length,
          operatorsUnknown: 0,
          basis: u.kind, basisS: Math.round(u.basis), basisIgnitionS: Math.round(u.basisIgn * k),
          utilisation: u.basis > 0 ? Math.min(100, r1((100 * u.basisIgn * k) / u.basis)) : null,
        });
      }
    }
    return { rows, tz: TZ, truncated: false };
  }

  async reportEvents(q: EventReportQuery): Promise<ReportResult<EventRow>> {
    this.fresh();
    this.requireViewer(q.tenantId);
    if (checkReportRange(q.from, q.to)) throw new DataError("invalid_report_range");
    const sites = q.siteId ? this.siteTree(q.tenantId, q.siteId) : null;
    const from = zonedTimeToUtc(q.from, "00:00", TZ).getTime();
    const to = zonedTimeToUtc(addDays(q.to, 1), "00:00", TZ).getTime();
    const ops = this.a.operators().operators;
    const rows = this.a
      .alarms()
      .filter((x) => x.tenantId === q.tenantId && this.a.alarmVisible(x))
      .filter((x) => Date.parse(x.occurredAt) >= from && Date.parse(x.occurredAt) < to)
      .filter((x) => !sites || (x.siteId !== null && sites.has(x.siteId)))
      .filter((x) => q.vehicleIds.length === 0 || (x.vehicleId !== null && q.vehicleIds.includes(x.vehicleId)))
      .filter((x) => !q.operatorId || x.operatorId === q.operatorId)
      .filter((x) => q.types.length === 0 || q.types.includes(x.type))
      .sort((x, y) => Date.parse(y.occurredAt) - Date.parse(x.occurredAt) || Number(y.id) - Number(x.id))
      .map<EventRow>((x) => {
        const visible = x.operatorId !== null && this.operatorVisible(x.operatorId);
        const v = this.a.vehicles().find((y) => y.id === x.vehicleId);
        const g = x.geofenceId ? this.a.geofences().find((z) => z.id === x.geofenceId) : undefined;
        return {
          id: x.id, occurredAt: x.occurredAt, type: x.type, severity: x.severity, status: x.status,
          vehicleId: x.vehicleId ?? "", fleetNo: v?.fleetNo ?? null, siteId: x.siteId,
          siteName: (x.siteId && this.a.canAccessSite(x.siteId) ? this.a.sites().find((s) => s.id === x.siteId)?.name : null) ?? null,
          operatorId: visible ? x.operatorId : null, operatorName: visible ? (ops.find((o) => o.id === x.operatorId)?.fullName ?? null) : null,
          offHours: x.offHours, delayed: x.delayed, timeUnknown: x.timeUnknown, repeatCount: x.repeatCount,
          ackedAt: x.ackedAt, ackNote: x.ackNote, geofenceId: x.geofenceId, geofenceName: g?.name ?? null, shiftName: x.shiftName ?? null,
        };
      });
    return { rows, tz: TZ, truncated: false };
  }

  async reportRoute(q: RouteQuery): Promise<ReportResult<RoutePoint>> {
    this.fresh();
    this.requireViewer(q.tenantId);
    const v = this.a.vehicles().find((x) => x.id === q.vehicleId && x.tenantId === q.tenantId);
    if (!v || !this.a.canAccessSite(v.siteId)) throw new DataError("forbidden");
    if (checkRouteWindow(q.from, q.to)) throw new DataError("invalid_report_range");
    const anchor = this.anchor(v.siteId);
    if (!anchor || !this.hasDevice(v)) return { rows: [], tz: TZ, truncated: false };
    const from = Date.parse(q.from);
    const to = Math.min(Date.parse(q.to), this.a.now());
    const out: RoutePoint[] = [];
    const ops = this.a.operators().operators;
    // A 140 m × 70 m loop around the yard at ~7 km/h; 6 min driving, 4 min stopped; off 12:00–12:45 local.
    const pos = this.yardLoop(v, anchor);
    let dist = 0;
    const drivers = this.driversOf(v);
    let dayKey = "";
    let dayStart = 0;
    let dayEnd = 0;
    for (let t = Math.ceil(from / 2000) * 2000; t < to && out.length < REPORT_LIMITS.routeMaxPoints; t += 2000) {
      if (t >= dayEnd) {
        dayKey = localParts(new Date(t), TZ).date;
        ({ ds: dayStart, de: dayEnd } = this.dayWindow(dayKey));
      }
      const lp = { date: dayKey, minutes: Math.floor((t - dayStart) / 60_000) };
      if (lp.minutes < 7 * 60 || lp.minutes >= 15 * 60 + 30 || isoDay(lp.date) === 7) continue;
      const lunch = lp.minutes >= 12 * 60 && lp.minutes < 12 * 60 + 45;
      const inCycle = (lp.minutes % 10) < 6;
      const moving = !lunch && inCycle;
      // Points every 2 s while driving, every 30 s while stopped (like the device cadence).
      if (!moving && Math.floor(t / 1000) % 30 !== 0) continue;
      if (moving) dist += (7 / 3.6) * 2;
      const [lat, lon] = pos(dist);
      const driver = drivers[lp.minutes < 12 * 60 ? 0 : 1] ?? null;
      const visible = driver !== null && this.operatorVisible(driver);
      out.push({
        ts: new Date(t).toISOString(), lat, lon, speedKmh: moving ? 6 + 2 * noise(`${t}`) : 0, ignition: !lunch, src: "gnss", acc: 4,
        operatorId: visible ? driver : null, operatorName: visible ? (ops.find((o) => o.id === driver)?.fullName ?? null) : null,
      });
      // One sample GNSS jump a day (the console filters it out and says so).
      if (lp.minutes === 10 * 60 + 2 && Math.floor(t / 1000) % 60 === 0) out.push({ ...out[out.length - 1]!, ts: new Date(t + 1000).toISOString(), lat: lat + 0.02 });
    }
    return { rows: out, tz: TZ, truncated: false };
  }

  /** The sample vehicle's 140 m × 70 m loop around its yard (offset per vehicle): position after `m` metres. */
  private yardLoop(v: Vehicle, anchor: [number, number]): (m: number) => [number, number] {
    const mPerLat = 1 / 111_195;
    const mPerLon = 1 / (111_195 * Math.cos((anchor[0] * Math.PI) / 180));
    const ox = (noise(v.id) - 0.5) * 80;
    const oy = (noise(`${v.id}|y`) - 0.5) * 60;
    return (m: number): [number, number] => {
      const d = ((m % YARD_LOOP_M) + YARD_LOOP_M) % YARD_LOOP_M;
      const [x, y] = d < 140 ? [d, 0] : d < 210 ? [140, d - 140] : d < 350 ? [140 - (d - 210), 70] : [0, 70 - (d - 350)];
      return [anchor[0] + (oy + y - 35) * mPerLat, anchor[1] + (ox + x - 70) * mPerLon];
    };
  }

  /**
   * Sample heatmap (ADR 0024). Usage: each sample vehicle's moving seconds of the day spread along its yard
   * loop, denser at the corners (turning) and on the loading side. Events: the visible demo alarms of the
   * scope; an alarm without its own point is placed on its vehicle's loop (where the device event would put
   * it), checklist alarms have no location. Same scope, range and weight rules as report_heatmap().
   */
  async reportHeatmap(q: HeatmapQuery): Promise<HeatmapResult> {
    this.fresh();
    this.requireViewer(q.tenantId);
    if (checkReportRange(q.from, q.to, HEATMAP_LIMITS.maxDays)) throw new DataError("invalid_report_range");
    const pts: { lat: number; lon: number; weight: number; count?: number; vehicleId: string }[] = [];
    let unlocated = 0;
    if (q.mode === "usage") {
      const vs = this.scopeVehicles(q);
      if (vs.length * dayRange(q.from, q.to).length > HEATMAP_LIMITS.maxVehicleDays) throw new DataError("report_too_large");
      const today = this.today();
      const n = YARD_LOOP_M / 2;
      for (const v of vs.filter((x) => this.hasDevice(x))) {
        const anchor = this.anchor(v.siteId);
        if (!anchor) continue;
        const pos = this.yardLoop(v, anchor);
        for (const day of dayRange(q.from, q.to < today ? q.to : today)) {
          const mov = this.usage(v, day).mov;
          if (mov <= 0) continue;
          // One sample position per 2 m of loop; corners and the loading side (far long edge) get more time.
          const shares = Array.from({ length: n }, (_, i) => {
            const d = i * 2;
            const corner = [0, 140, 210, 350, YARD_LOOP_M].reduce((m, c) => Math.min(m, Math.abs(d - c)), Infinity);
            const dock = d >= 230 && d < 300 ? 1.2 : 0;
            return 1 + 2.5 * Math.exp(-(corner * corner) / 60) + dock + 0.6 * noise(`${v.id}|${day}|${i}`);
          });
          const total = shares.reduce((a, b) => a + b, 0);
          shares.forEach((sh, i) => {
            const w = Math.round((mov * sh) / total);
            if (w <= 0) return;
            const [lat, lon] = pos(i * 2 + 1);
            pts.push({ lat, lon, weight: w, count: w, vehicleId: v.id });
          });
        }
      }
    } else {
      const sites = q.siteId ? this.siteTree(q.tenantId, q.siteId) : null;
      const from = zonedTimeToUtc(q.from, "00:00", TZ).getTime();
      const to = zonedTimeToUtc(addDays(q.to, 1), "00:00", TZ).getTime();
      for (const x of this.a.alarms()) {
        if (x.tenantId !== q.tenantId || !this.a.alarmVisible(x)) continue;
        const at = Date.parse(x.occurredAt);
        if (at < from || at >= to) continue;
        if (sites && (x.siteId === null || !sites.has(x.siteId))) continue;
        if (q.vehicleIds.length && (x.vehicleId === null || !q.vehicleIds.includes(x.vehicleId))) continue;
        // Like SQL: without a type filter, checklist alarms (never located) are left out.
        if (q.types.length ? !q.types.includes(x.type) : x.type.startsWith("checklist")) continue;
        const v = x.vehicleId ? this.a.vehicles().find((y) => y.id === x.vehicleId) : undefined;
        let p: [number, number] | null = x.lat !== null && x.lon !== null ? [x.lat, x.lon] : null;
        if (!p && v && !x.type.startsWith("checklist")) {
          const anchor = this.anchor(x.siteId ?? v.siteId);
          if (anchor) p = this.yardLoop(v, anchor)(noise(`${x.id}|pos`) * YARD_LOOP_M);
        }
        if (!p) {
          unlocated++;
          continue;
        }
        pts.push({ lat: p[0], lon: p[1], weight: heatmapEventWeight(x.type, x.severity), vehicleId: x.vehicleId ?? "" });
      }
    }
    return heatmapAggregate(pts, { mode: q.mode, tz: TZ, unlocated });
  }

  private anchor(siteId: string): [number, number] | null {
    for (const id of this.ancestors(siteId)) {
      const s = this.a.sites().find((x) => x.id === id);
      if (s && s.lat !== null && s.lon !== null) return [s.lat, s.lon];
    }
    return null;
  }

  /** Shift occurrences of a vehicle in [from, until) and whether it was used / checked (sample). */
  private occurrences(v: Vehicle, from: number, until: number) {
    const shifts = this.shiftsOf(v.siteId);
    const now = this.a.now();
    return shiftInstances(shifts, new Date(from), new Date(Math.min(until, now)))
      .filter((i) => i.startsAt.getTime() >= from && i.startsAt.getTime() < Math.min(until, now))
      .map((i) => {
        const day = localParts(i.startsAt, TZ).date;
        const used = this.usage(v, day).ign > 0 && noise(`${v.id}|${i.shiftId}|${day}|u`) > 0.25;
        return { name: i.name, startsAt: i.startsAt.getTime(), used, checked: used && noise(`${v.id}|${i.shiftId}|${day}|c`) > 0.13 };
      });
  }

  async reportChecklist(q: ReportQuery): Promise<ReportResult<ChecklistComplianceRow>> {
    this.fresh();
    this.requireViewer(q.tenantId);
    if (checkReportRange(q.from, q.to)) throw new DataError("invalid_report_range");
    const from = zonedTimeToUtc(q.from, "00:00", TZ).getTime();
    const to = zonedTimeToUtc(addDays(q.to, 1), "00:00", TZ).getTime();
    const faults = (await this.a.faults(q.tenantId)).filter((f) => {
      const c = Date.parse(f.createdAt);
      const r = f.resolvedAt ? Date.parse(f.resolvedAt) : NaN;
      return (c >= from && c < to) || (r >= from && r < to);
    });
    const rows: ChecklistComplianceRow[] = [];
    for (const v of this.scopeVehicles(q).filter((x) => this.hasDevice(x)).sort((x, y) => x.fleetNo.localeCompare(y.fleetNo))) {
      const occ = this.occurrences(v, from, to);
      const hasShifts = this.shiftsOf(v.siteId).length > 0;
      const names = hasShifts ? [...new Set(occ.map((o) => o.name))] : [null];
      for (const name of names) {
        const mine = occ.filter((o) => o.name === name);
        const used = mine.filter((o) => o.used).length;
        const checked = mine.filter((o) => o.checked).length;
        const vf = faults.filter((f) => f.vehicleId === v.id && (name === null || name === this.shiftsOf(v.siteId)[0]?.name));
        const resolved = vf.filter((f) => f.resolvedAt && Date.parse(f.resolvedAt) >= from && Date.parse(f.resolvedAt) < to);
        const submissions = hasShifts ? checked : Math.round(dayRange(q.from, q.to).filter((d) => this.usage(v, d).ign > 0).length * 0.9);
        if (used === 0 && submissions === 0 && vf.length === 0) continue;
        rows.push({
          vehicleId: v.id, fleetNo: v.fleetNo, siteId: v.siteId, shiftName: name, hasShifts,
          shiftsUsed: hasShifts ? used : null, shiftsChecked: hasShifts ? checked : null, missed: hasShifts ? used - checked : null,
          submissions, compliance: hasShifts && used > 0 ? r1((100 * checked) / used) : null,
          faultsOpened: vf.filter((f) => Date.parse(f.createdAt) >= from && Date.parse(f.createdAt) < to).length,
          faultsResolved: resolved.length,
          mttrH: resolved.length ? r1(resolved.reduce((s, f) => s + (Date.parse(f.resolvedAt!) - Date.parse(f.createdAt)) / 3600_000, 0) / resolved.length) : null,
        });
      }
    }
    return { rows, tz: TZ, truncated: false };
  }

  /* ---------- KPI panel ---------- */

  async getKpiPanel(tenantId: string, siteId: string | null, period: KpiPeriod): Promise<KpiPanel> {
    this.fresh();
    if (!this.a.userId() || !this.a.hasTenantRole(tenantId, "fleet_manager")) throw new DataError("forbidden");
    const n = period === "today" ? 1 : period === "7d" ? 7 : period === "30d" ? 30 : 0;
    if (!n) throw new DataError("invalid_report_range");
    const now = this.a.now();
    const today = this.today();
    const s = Math.max(n, 7);
    const curFrom = zonedTimeToUtc(addDays(today, -(n - 1)), "00:00", TZ).getTime();
    const prevFrom = zonedTimeToUtc(addDays(today, -(2 * n - 1)), "00:00", TZ).getTime();
    const prevTo = now - n * DAY;
    const sites = this.siteTree(tenantId, siteId);
    const all = this.a.vehicles().filter((v) => v.tenantId === tenantId && sites.has(v.siteId) && v.active);
    const fleet = all.filter((v) => this.hasDevice(v));
    const windowUsage = (from: number, until: number) => {
      let ign = 0, basis = 0, bi = 0;
      const active = new Set<string>();
      for (let d = localParts(new Date(from), TZ).date; this.dayWindow(d).ds < until; d = addDays(d, 1)) {
        for (const v of fleet) {
          const u = this.usage(v, d, from, until);
          ign += u.ign;
          basis += u.basis;
          bi += u.basisIgn;
          if (u.ign > 0) active.add(v.id);
        }
      }
      return { ign, basis, bi, active: active.size };
    };
    const alarms = this.a.alarms().filter((x) => x.tenantId === tenantId && this.a.alarmVisible(x) && (!siteId || (x.siteId !== null && sites.has(x.siteId))));
    const count = (types: readonly string[], from: number, until: number) =>
      alarms.filter((x) => types.includes(x.type) && Date.parse(x.occurredAt) >= from && Date.parse(x.occurredAt) < until).length;
    const occAll = fleet.flatMap((v) => this.occurrences(v, Math.min(prevFrom, zonedTimeToUtc(addDays(today, -(s - 1)), "00:00", TZ).getTime()), now));
    const occ = (from: number, until: number) => occAll.filter((o) => o.startsAt >= from && o.startsAt < until);
    const pct = (a: number, b: number) => (b > 0 ? r1((100 * a) / b) : null);
    const cur = windowUsage(curFrom, now);
    const prev = windowUsage(prevFrom, prevTo);
    const curOcc = occ(curFrom, now);
    const prevOcc = occ(prevFrom, prevTo);
    const lift = fleet.find((v) => this.hasDevice(v)?.deviceId === "DEV-SMP-1001");
    const overloads = (from: number, until: number) => {
      if (!lift) return 0;
      let k = 0;
      for (let d = localParts(new Date(from), TZ).date; this.dayWindow(d).ds < until; d = addDays(d, 1)) if (noise(`${lift.id}|${d}|ovl`) > 0.8) k++;
      return k;
    };
    const geo = EVENT_GROUP_TYPES.geofence;
    const battery = fleet.map((v) => this.a.live().find((l) => l.vehicleId === v.id && l.soc != null)).filter((l): l is LiveSnapshot => !!l);
    const series: KpiSeriesPoint[] = dayRange(addDays(today, -(s - 1)), today).map((day) => {
      const { ds, de } = this.dayWindow(day);
      const u = windowUsage(ds, Math.min(de, now));
      const o = occ(ds, Math.min(de, now));
      const used = o.filter((x) => x.used).length;
      return {
        day,
        utilisation: pct(u.bi, u.basis),
        active: u.active,
        impacts: count(["impact"], ds, de),
        checklist: pct(o.filter((x) => x.checked).length, used),
        geofence: count(geo, ds, de),
        deepDischarges: 0,
        avgSoc: battery.length ? r1(battery.reduce((a, l) => a + (l.soc ?? 0), 0) / battery.length) : null,
        overloads: overloads(ds, Math.min(de, now)),
      };
    });
    const ops = this.a.operators();
    const docsVisible = this.a.hasTenantRole(tenantId, "safety");
    const opsInScope = ops.operators.filter((o) => o.tenantId === tenantId && o.active && !o.anonymized && o.leftOn === null && (!siteId || (o.siteId !== null && sites.has(o.siteId))));
    const docDays = (d: string) => Math.round((Date.parse(`${d}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY);
    const faults = (await this.a.faults(tenantId)).filter((f) => f.status === "open" && f.critical && (!siteId || (f.siteId !== null && sites.has(f.siteId))));
    const usedCur = curOcc.filter((x) => x.used).length;
    const usedPrev = prevOcc.filter((x) => x.used).length;
    const anyShifts = fleet.some((v) => this.shiftsOf(v.siteId).length > 0);
    const kinds = [...new Set(fleet.map((v) => this.kindOf(v.siteId, "auto")))];
    return {
      tz: TZ, period, from: new Date(curFrom).toISOString(), to: new Date(now).toISOString(),
      prevFrom: new Date(prevFrom).toISOString(), prevTo: new Date(prevTo).toISOString(),
      vehicles: { total: all.length, withDevice: fleet.length, active: cur.active, prevActive: prev.active },
      utilisation: { value: pct(cur.bi, cur.basis), prev: pct(prev.bi, prev.basis), hours: r1(cur.ign / 3600), basis: kinds.length === 1 ? kinds[0]! : kinds.length > 1 ? "mixed" : "work" },
      impacts: {
        value: count(["impact"], curFrom, now), prev: count(["impact"], prevFrom, prevTo),
        per100h: cur.ign > 0 ? Math.round((count(["impact"], curFrom, now) * 360000 * 100) / cur.ign) / 100 : null,
        prevPer100h: prev.ign > 0 ? Math.round((count(["impact"], prevFrom, prevTo) * 360000 * 100) / prev.ign) / 100 : null,
      },
      checklist: { value: pct(curOcc.filter((x) => x.checked).length, usedCur), prev: pct(prevOcc.filter((x) => x.checked).length, usedPrev), hasShifts: anyShifts, used: usedCur, missed: usedCur - curOcc.filter((x) => x.checked).length },
      faults: { openCritical: faults.length, maintenanceVehicles: new Set(faults.map((f) => f.vehicleId)).size },
      geofence: {
        value: count(geo, curFrom, now), prev: count(geo, prevFrom, prevTo),
        enter: count(["geofence_enter"], curFrom, now), exit: count(["geofence_exit"], curFrom, now), dwell: count(["geofence_dwell"], curFrom, now),
      },
      battery: battery.length
        ? { lowSoc: battery.filter((l) => (l.soc ?? 100) < REPORT_LIMITS.lowSocPct).length, deepDischarges: 0, prevDeepDischarges: 0, avgSoc: r1(battery.reduce((a, l) => a + (l.soc ?? 0), 0) / battery.length) }
        : null,
      lift: lift ? { overloads: overloads(curFrom, now), prev: overloads(prevFrom, prevTo) } : null,
      documents: docsVisible
        ? {
            expiring: new Set(ops.documents.filter((d) => opsInScope.some((o) => o.id === d.operatorId) && docDays(d.validUntil) >= 0 && docDays(d.validUntil) <= 30).map((d) => d.operatorId)).size,
            expired: new Set(ops.documents.filter((d) => d.required && opsInScope.some((o) => o.id === d.operatorId) && docDays(d.validUntil) < 0).map((d) => d.operatorId)).size,
          }
        : null,
      series,
    };
  }

  /* ---------- export log ---------- */

  readonly exportLog: (ReportExportEntry & { userId: string; at: string })[] = [];

  /** Like export_report(): run the report, count its rows, log, return. Nothing is logged when the report fails. */
  private async logged<R>(tenantId: string, report: ReportExportEntry["report"], format: ExportFormat, filters: Record<string, unknown>,
                          run: () => Promise<ReportResult<R>>, hasOperators: (r: R) => boolean): Promise<ReportResult<R>> {
    const uid = this.a.userId();
    if (!uid || !this.a.hasTenantRole(tenantId, "viewer")) throw new DataError("forbidden");
    const hourAgo = this.a.now() - 3600_000;
    if (this.exportLog.filter((e) => e.userId === uid && Date.parse(e.at) > hourAgo).length >= 120) throw new DataError("rate_limited");
    const res = await run();
    if (res.rows.length > REPORT_LIMITS.exportMaxRows) throw new DataError("export_too_large");
    this.exportLog.push({ tenantId, report, format, filters, rowCount: res.rows.length, includesOperators: res.rows.some(hasOperators), userId: uid, at: new Date(this.a.now()).toISOString() });
    return res;
  }

  exportUsage(q: ReportQuery, format: ExportFormat): Promise<ReportResult<UsageRow>> {
    const { tenantId, ...f } = q;
    return this.logged(tenantId, "usage", format, f, () => this.reportUsage(q), (r) => r.operatorNames.length > 0);
  }

  exportEvents(q: EventReportQuery, format: ExportFormat): Promise<ReportResult<EventRow>> {
    const { tenantId, ...f } = q;
    return this.logged(tenantId, "events", format, f, () => this.reportEvents(q), (r) => r.operatorName !== null);
  }

  exportRoute(q: RouteQuery, format: ExportFormat): Promise<ReportResult<RoutePoint>> {
    const { tenantId, ...f } = q;
    return this.logged(tenantId, "route", format, f, () => this.reportRoute(q), (r) => r.operatorName !== null);
  }

  /** Like export_report('heatmap'): the cells, logged with the cell count and the mode (never operators). */
  async exportHeatmap(q: HeatmapQuery, format: ExportFormat): Promise<HeatmapResult> {
    const { tenantId, ...f } = q;
    let out: HeatmapResult | null = null;
    await this.logged(tenantId, "heatmap", format, f, async () => {
      const r = await this.reportHeatmap(q);
      out = r;
      return { rows: r.cells, tz: r.tz, truncated: r.truncated };
    }, () => false);
    return out!;
  }

  exportChecklist(q: ReportQuery, format: ExportFormat): Promise<ReportResult<ChecklistComplianceRow>> {
    const { tenantId, ...f } = q;
    return this.logged(tenantId, "checklist", format, f, () => this.reportChecklist(q), () => false);
  }
}
